import { makeVar } from '@apollo/client';
import * as Sentry from '@sentry/browser';
import { keyBy, values, flatMap } from 'lodash';
import { change, hidden } from 'visibilityjs';
import api from '../../core/api';
import SeasonParticipant from 'models/season-participant';
import { isValidDate } from 'core/utils';

export const seasonMemberParticipationsFullVar = makeVar({
  isLoading: false,
  data: [],
  lastUpdate: null,
  isUpdating: false,
});
export const seasonMembersVar = makeVar({
  isLoading: false,
  data: [],
  lastUpdate: null,
  isUpdating: false,
});
export const seasonMemberContactsVar = makeVar({
  isLoading: false,
  data: [],
  lastUpdate: null,
  isUpdating: false,
});
export const seasonParicipationsVar = makeVar({
  isLoading: false,
  data: [],
  lastUpdate: null,
  isUpdating: false,
});
export const seasonCustomDataVar = makeVar({
  isLoading: false,
  data: [],
  lastUpdate: null,
  isUpdating: false,
});

export type MemberToDelete = {
  id: string;
  firstName: string;
  lastName: string;
  canBeDeleted: boolean;
  deletableParticipations: SeasonParticipant[];
  participationsToDelete: {
    seasonParticipantId: number;
    seasonParticipantType: string;
  }[];
};

enum RefresherUpdateStyle {
  MERGE = 1,
  REPLACE,
}

type Refresher = {
  url: string;
  waitingOnResponse: boolean;
  lastRun: number;
  lastUpdate: number;
  varFunction: any;
  keyField: string;
  updateStyle: RefresherUpdateStyle;
  refreshFrequency: number;
  refreshBackoff: number[];
  consecutiveErrors: number;
};

const _refreshers = {};
let _refreshersTimer = null;

const DEFAULT_REFRESH_BACKOFF = [3, 7, 15, 30, 300].map((v) => v * 1000);

function changeDataSync() {
  if (hidden()) {
    stopRefreshers();
  } else {
    startRefreshers();
  }
}

change(changeDataSync);

export async function exportParticipants(orgId, seasonId, exportRequest) {
  return api.axios.post(
    `/organizations/${orgId}/seasons/${seasonId}/participations_export`,
    exportRequest
  );
}

export function setLoaderWithRefresh(
  name: string,
  varFunction: any,
  url: string,
  keyField: string
) {
  const refresher = {
    url: url,
    waitingOnResponse: false,
    lastRun: 0,
    consecutiveErrors: 0,
    lastUpdate: 0,
    varFunction: varFunction,
    keyField: keyField,
    updateStyle: RefresherUpdateStyle.MERGE,
    refreshFrequency: DEFAULT_REFRESH_BACKOFF[0],
    refreshBackoff: DEFAULT_REFRESH_BACKOFF,
  };

  if (_refreshers[name]) {
    if (_refreshers[name].url !== url) {
      varFunction({
        ...varFunction(),
        data: [],
      });
      _refreshers[name] = refresher;
    }
  } else {
    _refreshers[name] = refresher;
  }

  callRefresher(_refreshers[name]);
  startRefreshers();
}

export async function deleteSeasonParticipants(
  mutation,
  orgId: number,
  membersToDelete: MemberToDelete[]
) {
  await mutation({
    variables: {
      organizationId: orgId,
      deleteMemberSeasonParticipants: membersToDelete.map((x) => ({
        memberId: x.id,
        seasonParticipants: x.participationsToDelete,
      })),
    },
    update() {
      updateCacheForParticipantDelete(membersToDelete);
    },
  });
}

function updateCacheForParticipantDelete(membersToDelete: MemberToDelete[]) {
  // Can be simplified when cache connected to event stream
  membersToDelete.forEach((memberToDelete) => {
    const { participationsToDelete } = memberToDelete;

    const updatedSeasonParticipations = seasonParicipationsVar().data.filter(
      (val) =>
        !participationsToDelete.find(
          (del) => del.seasonParticipantId === val.seasonParticipantId
        )
    );

    seasonParicipationsVar({
      ...seasonParicipationsVar(),
      data: updatedSeasonParticipations,
    });

    const updatedCustomData = seasonCustomDataVar().data.filter(
      (val) =>
        !participationsToDelete.find(
          (del) => del.seasonParticipantId === val.memberId
        )
    );

    seasonCustomDataVar({
      ...seasonCustomDataVar(),
      data: updatedCustomData,
    });

    // Don't need to remove memeber since it filters out if no participations
  });
}

export function updateSeasonParticipantCache(
  updatedFullSeasonMemberParticipants
) {
  const updatedSeasonParticipations = flatMap(
    updatedFullSeasonMemberParticipants,
    (seasonMemberFull) =>
      seasonMemberFull.seasonParticipants.map((participant) => {
        participant = Object.assign({}, participant);
        participant.memberId = +seasonMemberFull.id;

        return participant;
      })
  );

  const updatedSeasonMembers = updatedFullSeasonMemberParticipants.map(
    (seasonMemberFull) => {
      const participations = seasonMemberFull['seasonParticipants'];
      seasonMemberFull = Object.assign({}, seasonMemberFull);
      delete seasonMemberFull['seasonParticipants'];
      seasonMemberFull.id = +seasonMemberFull.id;

      if (participations?.length === 0) {
        seasonMemberFull.isDeleted = true;
      }
      return seasonMemberFull;
    }
  );

  mergeInfo(
    'seasonParticipantId',
    seasonParicipationsVar,
    updatedSeasonParticipations
  );
  mergeInfo('id', seasonMembersVar, updatedSeasonMembers);
}

function determineLastUpdate(lastUpdate: number, data: any) {
  let maxDateFromResults = (data || []).reduce(
    (currentMax, nextValue) =>
      nextValue.updatedAt && nextValue.updatedAt > currentMax
        ? nextValue.updatedAt
        : currentMax,
    0
  );

  maxDateFromResults =
    maxDateFromResults && maxDateFromResults.endswith('Z')
      ? maxDateFromResults
      : maxDateFromResults + 'Z';

  return Math.max(
    lastUpdate,
    maxDateFromResults ? new Date(maxDateFromResults).getTime() : 0
  );
}

function mergeInfo(keyField: string, dataVarFunction: any, responseData: any) {
  const updatedInfo = keyBy(
    responseData.filter((value) => value['isDeleted'] !== true),
    keyField
  );
  const deletedData = responseData.filter(
    (value) => value['isDeleted'] === true
  );
  const remainingInfo = keyBy(
    dataVarFunction().data.filter(
      (val) => !deletedData.find((del) => del[keyField] === val[keyField])
    ),
    keyField
  );

  const newData = values({ ...remainingInfo, ...updatedInfo });

  dataVarFunction({
    ...dataVarFunction(),
    data: newData,
    isLoading: false,
    isUpdating: false,
  });
}

function callRefresher(refresher: Refresher) {
  // ###### console logs left in place for future debugging ######
  // console.log('calling refresher', refresher);
  let url = refresher.url;

  const isInitialLoad = refresher.lastUpdate === 0;
  const updatedDate = new Date(refresher.lastUpdate);
  const updateTime =
    !isInitialLoad && isValidDate(updatedDate)
      ? updatedDate.toISOString()
      : null;
  url += updateTime ? (url && url.indexOf('?') > 0 ? '&' : '?') : '';
  url += updateTime ? `lastUpdate=${updateTime}` : '';

  const maxRefreshFrequency =
    refresher.refreshBackoff[refresher.refreshBackoff.length - 1];
  const indexOfRefreshFrequency = refresher.refreshBackoff.indexOf(
    refresher.refreshFrequency
  );
  const nextRefreshFrequency =
    refresher.refreshBackoff[indexOfRefreshFrequency + 1];

  if (isInitialLoad) {
    refresher.varFunction({ ...refresher.varFunction(), isLoading: true });
  } else {
    refresher.varFunction({ ...refresher.varFunction(), isUpdating: true });
  }

  const newLastUpdate = new Date().getTime();
  refresher.waitingOnResponse = true;
  // console.log('call for data ', url, isInitialLoad, maxRefreshFrequency, nextRefreshFrequency)
  api
    .get(`${url}`)
    .then((result) => {
      const requestTime = new Date().getTime() - newLastUpdate;
      const startOfRequestServerTime = new Date(
        new Date(result.time).getTime() - requestTime
      ).getTime();

      const data = result.data as any[];

      refresher.lastRun = new Date().getTime();

      if (result.status === 200 && data.length > 0) {
        refresher.consecutiveErrors = 0;

        const lastUpdatedTS = determineLastUpdate(
          startOfRequestServerTime,
          data
        );

        refresher.lastUpdate = lastUpdatedTS;
        refresher.refreshFrequency = refresher.refreshBackoff[0];
        mergeInfo(refresher.keyField, refresher.varFunction, data);
        refresher.waitingOnResponse = false;

        return data;
      } else if (result.status === 304 || data.length === 0) {
        refresher.consecutiveErrors = 0;
        refresher.lastUpdate = startOfRequestServerTime;

        if (refresher.refreshFrequency < maxRefreshFrequency) {
          refresher.refreshFrequency = nextRefreshFrequency;
        }

        refresher.varFunction({
          ...refresher.varFunction(),
          isLoading: false,
          isUpdating: false,
        });

        refresher.waitingOnResponse = false;

        return refresher.varFunction();
      }
    })
    .catch((e) => {
      refresher.consecutiveErrors += 1;
      refresher.lastRun = new Date().getTime();
      refresher.waitingOnResponse = false;

      if (refresher.consecutiveErrors < 11) {
        refresher.lastRun = 0;
      } else if (refresher.consecutiveErrors === 11) {
        refresher.refreshFrequency = refresher.refreshBackoff[0];
      } else if (refresher.refreshFrequency < maxRefreshFrequency) {
        refresher.refreshFrequency = nextRefreshFrequency;
      }

      Sentry.captureException(e, {
        level: 'warning',
        extra: {
          lastRun: refresher.lastRun,
          consecutiveErrors: refresher.consecutiveErrors,
          lastUpdate: refresher.lastUpdate,
          refreshFrequency: refresher.refreshFrequency,
        },
      });

      return refresher.varFunction();
    });
}

function checkRefreshers() {
  const time = new Date().getTime();

  // console.log('checking refreshers', _refreshers);

  Object.keys(_refreshers).map((name) => {
    const refresher = _refreshers[name];

    if (
      !refresher.waitingOnResponse &&
      refresher.lastRun > 0 &&
      time - refresher.lastRun > refresher.refreshFrequency
    ) {
      callRefresher(refresher);
    }
  });

  _refreshersTimer = setTimeout(checkRefreshers, 1000);
}

export function startRefreshers() {
  if (!_refreshersTimer) {
    _refreshersTimer = setTimeout(checkRefreshers, 500);
  }
}

export function stopRefreshers() {
  clearTimeout(_refreshersTimer);
  _refreshersTimer = null;
}
